import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Users, Eye, Clock, MousePointerClick, TrendingUp, Globe, RefreshCw } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { loadPageViewsFn } from "./-dashboard.admin.traffic.functions";

export const Route = createFileRoute("/dashboard/admin/traffic")({
 component: TrafficPage,
});

type Range = "24h" | "7d" | "30d";

interface PageRow { path: string; views: number; avg_seconds: number }
interface DayRow { day: string; views: number; visitors: number }
interface Totals { views: number; visitors: number; avg_seconds: number; bounce_pct: number }

function rangeStart(range: Range): Date {
 const now = new Date();
 const d = new Date(now);
 if (range === "24h") d.setHours(d.getHours() - 24);
 else if (range === "7d") d.setDate(d.getDate() - 7);
 else d.setDate(d.getDate() - 30);
 return d;
}

function TrafficPage() {
 const [range, setRange] = useState<Range>("7d");
 const [loading, setLoading] = useState(true);
 const [totals, setTotals] = useState<Totals>({ views: 0, visitors: 0, avg_seconds: 0, bounce_pct: 0 });
 const [topPages, setTopPages] = useState<PageRow[]>([]);
 const [topReferrers, setTopReferrers] = useState<Array<{ referrer: string; count: number }>>([]);
 const [recent, setRecent] = useState<any[]>([]);
 const [series, setSeries] = useState<DayRow[]>([]);

 const load = async () => {
 setLoading(true);
 const since = rangeStart(range).toISOString();

 // Pull rows for the window (newest first, bounded to 20000) from Aurora.
 const all: Array<{ id: number; session_id: string; path: string; referrer: string | null; duration_ms: number | null; viewed_at: string }> =
 ((await loadPageViewsFn({ data: { since } })) ?? []) as any[];

 // Totals
 const sessions = new Map<string, number>();
 const pageMap = new Map<string, { views: number; total_ms: number; samples: number }>();
 const refMap = new Map<string, number>();
 const dayMap = new Map<string, { views: number; sessions: Set<string> }>();
 let bounceSessions = 0;

 for (const r of all) {
 sessions.set(r.session_id, (sessions.get(r.session_id) ?? 0) + 1);
 const p = pageMap.get(r.path) ?? { views: 0, total_ms: 0, samples: 0 };
 p.views += 1;
 if (typeof r.duration_ms === "number" && r.duration_ms > 0 && r.duration_ms < 60 * 60 * 1000) {
 p.total_ms += r.duration_ms; p.samples += 1;
 }
 pageMap.set(r.path, p);

 const ref = (r.referrer || "Direct").replace(/^https?:\/\//, "").replace(/\/.*$/, "") || "Direct";
 refMap.set(ref, (refMap.get(ref) ?? 0) + 1);

 const day = r.viewed_at.slice(0, 10);
 const d = dayMap.get(day) ?? { views: 0, sessions: new Set<string>() };
 d.views += 1; d.sessions.add(r.session_id);
 dayMap.set(day, d);
 }

 sessions.forEach((count) => { if (count === 1) bounceSessions++; });

 const totalDurations = all
 .map((r) => r.duration_ms)
 .filter((d): d is number => typeof d === "number" && d > 0 && d < 60 * 60 * 1000);
 const avg_seconds = totalDurations.length
 ? Math.round(totalDurations.reduce((a, b) => a + b, 0) / totalDurations.length / 1000)
 : 0;

 setTotals({
 views: all.length,
 visitors: sessions.size,
 avg_seconds,
 bounce_pct: sessions.size ? Math.round((bounceSessions / sessions.size) * 100) : 0,
 });

 setTopPages(
 Array.from(pageMap.entries())
 .map(([path, v]) => ({
 path,
 views: v.views,
 avg_seconds: v.samples ? Math.round(v.total_ms / v.samples / 1000) : 0,
 }))
 .sort((a, b) => b.views - a.views)
 .slice(0, 15)
 );

 setTopReferrers(
 Array.from(refMap.entries())
 .map(([referrer, count]) => ({ referrer, count }))
 .sort((a, b) => b.count - a.count)
 .slice(0, 10)
 );

 setSeries(
 Array.from(dayMap.entries())
 .map(([day, v]) => ({ day, views: v.views, visitors: v.sessions.size }))
 .sort((a, b) => a.day.localeCompare(b.day))
 );

 setRecent(all.slice(0, 30));
 setLoading(false);
 };

 useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

 const maxDay = Math.max(1, ...series.map((s) => s.views));

 return (
 <AdminPageShell
 title="Site traffic"
 description="Live visitor analytics — pages, sessions, referrers, and dwell time."
 primaryAction={{ label: "Refresh", icon: RefreshCw, onClick: load }}
 >
 <div className="space-y-8">
 <div className="inline-flex border border-border bg-muted/40 p-1">
 {(["24h", "7d", "30d"] as Range[]).map((r) => (
 <button
 key={r}
 onClick={() => setRange(r)}
 className={`px-4 py-1.5 text-xs font-medium transition ${range === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
 >
 {r === "24h" ? "Last 24h" : r === "7d" ? "Last 7 days" : "Last 30 days"}
 </button>
 ))}
 </div>

 {/* KPIs */}
 <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
 <Kpi label="Visitors" value={totals.visitors.toLocaleString()} icon={<Users className="h-4 w-4" />} accent="from-primary/15" />
 <Kpi label="Page views" value={totals.views.toLocaleString()} icon={<Eye className="h-4 w-4" />} accent="from-emerald-500/15" />
 <Kpi label="Avg. time on page" value={`${totals.avg_seconds}s`} icon={<Clock className="h-4 w-4" />} accent="from-amber-500/15" />
 <Kpi label="Bounce rate" value={`${totals.bounce_pct}%`} icon={<MousePointerClick className="h-4 w-4" />} accent="from-rose-500/15" />
 </section>

 {/* Daily chart */}
 <section className="border border-border bg-card p-5">
 <div className="flex items-center justify-between mb-4">
 <h2 className="font-semibold">Traffic over time</h2>
 <span className="text-xs text-muted-foreground">{loading ? "Loading…" : `${series.length} day${series.length === 1 ? "" : "s"}`}</span>
 </div>
 {series.length === 0 ? (
 <p className="text-sm text-muted-foreground py-10 text-center">No traffic recorded in this window yet.</p>
 ) : (
 <div className="flex items-end gap-1 h-40">
 {series.map((s) => (
 <div key={s.day} className="flex-1 flex flex-col items-center gap-1 group">
 <div className="w-full flex flex-col justify-end h-32">
 <div
 className="w-full bg-gradient-to-t from-primary to-primary/60 transition-all group-hover:opacity-80"
 style={{ height: `${Math.max(2, (s.views / maxDay) * 100)}%` }}
 title={`${s.day}: ${s.views} views, ${s.visitors} visitors`}
 />
 </div>
 <span className="text-[10px] text-muted-foreground">{s.day.slice(5)}</span>
 </div>
 ))}
 </div>
 )}
 </section>

 {/* Top pages */}
 <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <div className="border border-border bg-card overflow-hidden">
 <div className="px-5 py-4 border-b border-border flex items-center gap-2">
 <Eye className="h-4 w-4 text-primary" />
 <h2 className="font-semibold">Top pages</h2>
 </div>
 {topPages.length === 0 ? (
 <p className="text-sm text-muted-foreground p-6 text-center">No page views yet.</p>
 ) : (
 <ul className="divide-y divide-border">
 {topPages.map((p) => (
 <li key={p.path} className="px-5 py-3 flex items-center justify-between gap-3">
 <div className="min-w-0">
 <p className="text-sm font-medium truncate">{p.path}</p>
 <p className="text-xs text-muted-foreground">{p.avg_seconds}s avg time</p>
 </div>
 <span className="text-sm font-semibold tabular-nums">{p.views.toLocaleString()}</span>
 </li>
 ))}
 </ul>
 )}
 </div>

 <div className="border border-border bg-card overflow-hidden">
 <div className="px-5 py-4 border-b border-border flex items-center gap-2">
 <Globe className="h-4 w-4 text-emerald-500" />
 <h2 className="font-semibold">Top referrers</h2>
 </div>
 {topReferrers.length === 0 ? (
 <p className="text-sm text-muted-foreground p-6 text-center">No referrer data yet.</p>
 ) : (
 <ul className="divide-y divide-border">
 {topReferrers.map((r) => (
 <li key={r.referrer} className="px-5 py-3 flex items-center justify-between gap-3">
 <span className="text-sm truncate">{r.referrer}</span>
 <span className="text-sm font-semibold tabular-nums">{r.count.toLocaleString()}</span>
 </li>
 ))}
 </ul>
 )}
 </div>
 </section>

 {/* Recent visits */}
 <section className="border border-border bg-card overflow-hidden">
 <div className="px-5 py-4 border-b border-border">
 <h2 className="font-semibold">Recent visits</h2>
 </div>
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">
 <tr>
 <th className="px-4 py-2 text-left">When</th>
 <th className="px-4 py-2 text-left">Path</th>
 <th className="px-4 py-2 text-left">Referrer</th>
 <th className="px-4 py-2 text-right">Time</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {recent.length === 0 && (
 <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No recent visits.</td></tr>
 )}
 {recent.map((r) => (
 <tr key={r.id}>
 <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{new Date(r.viewed_at).toLocaleString()}</td>
 <td className="px-4 py-2 truncate max-w-xs">{r.path}</td>
 <td className="px-4 py-2 text-muted-foreground truncate max-w-xs">{r.referrer || "Direct"}</td>
 <td className="px-4 py-2 text-right tabular-nums">{r.duration_ms ? `${Math.round(r.duration_ms / 1000)}s` : "—"}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </section>
 </div>
 </AdminPageShell>
 );
}

function Kpi({ label, value, icon, accent }: { label: string; value: string; icon: React.ReactNode; accent: string }) {
 return (
 <div className={`relative overflow-hidden border border-border bg-gradient-to-br ${accent} via-card to-card p-5`}>
 <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">{icon}{label}</div>
 <p className="text-3xl font-semibold mt-2 tabular-nums">{value}</p>
 </div>
 );
}
